import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import AppSection from "@/components/app/AppSection";
import AppStickyFooter from "@/components/app/AppStickyFooter";
import { StatusBadge } from "@/components/app/StatusBadge";
import { EmptyState } from "@/components/app/EmptyState";
import CTAButton from "@/components/app/CTAButton";
import AppModal from "@/components/app/AppModal";
import { api, formatApiError } from "@/lib/api";
import { Compass, Trash2, Lock, Rocket, Sparkles } from "lucide-react";

function fmtEur(n) {
    return `${(n || 0).toFixed(2).replace(".", ",")} €`;
}

export default function AppMissionDetail() {
    const { mid } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showLaunchInfo, setShowLaunchInfo] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/missions/${mid}`);
            setData(data);
        } catch (e) { setError(formatApiError(e)); }
    }, [mid]);

    useEffect(() => { load(); }, [load]);

    async function recompute() {
        setBusy(true); setError("");
        try { await api.post(`/missions/${mid}/preview`); await load(); }
        catch (e) { setError(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function addFromMissing() {
        setBusy(true); setError("");
        try { await api.post(`/missions/${mid}/add-from-missing`); await load(); }
        catch (e) { setError(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function removeItem(iid) {
        setBusy(true); setError("");
        try { await api.delete(`/missions/${mid}/items/${iid}`); await load(); }
        catch (e) { setError(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function doDelete() {
        setBusy(true); setError("");
        try { await api.delete(`/missions/${mid}`); navigate("/app/missions"); }
        catch (e) { setError(formatApiError(e)); setBusy(false); setConfirmDelete(false); }
    }

    if (!data) {
        return (
            <>
                <AppHeader title="Mission" subtitle="Chargement…" showBack onBack={() => navigate("/app/missions")} />
                <AppSection><div className="py-16 text-center text-sm text-md-text-2">Chargement de l’expédition…</div></AppSection>
            </>
        );
    }

    const { mission, items, summary, total_eur, item_count } = data;
    const locked = !!mission.locked;
    const empty = item_count === 0;

    return (
        <>
            <AppHeader
                title={mission.title || "Mission"}
                subtitle={locked ? "Mission verrouillée" : "Préparation"}
                showBack
                onBack={() => navigate("/app/missions")}
            />

            {error && (
                <AppSection>
                    <div className="rounded-lg border border-md-danger/30 bg-md-danger/10 px-3 py-2 text-xs text-md-danger" data-testid="mission-detail-error">{error}</div>
                </AppSection>
            )}

            {/* Hero summary */}
            <AppSection>
                <div className="rounded-2xl border border-md-border bg-md-surface p-6">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="md-overline">TON CLASSEUR EN ATTENTE</p>
                            <h2 className="mt-2 text-2xl font-black tracking-tight text-md-text sm:text-3xl">
                                {item_count} carte{item_count > 1 ? "s" : ""}
                            </h2>
                            <p className="mt-1 text-xs text-md-text-2">à dénicher chez les dresseurs.</p>
                        </div>
                        <div className="text-right">
                            <p className="md-overline">ESTIMATION</p>
                            <p className="mt-2 text-2xl font-black tracking-tight text-md-secondary sm:text-3xl">{fmtEur(total_eur)}</p>
                            <p className="mt-1 text-[10px] uppercase tracking-widest text-md-text-2">tarif indicatif</p>
                        </div>
                    </div>
                </div>
            </AppSection>

            {/* Summary by category */}
            {summary.length > 0 && (
                <AppSection title="Répartition par catégorie">
                    <ul className="space-y-2" data-testid="mission-summary-list">
                        {summary.map((s) => (
                            <li
                                key={s.category_id || "none"}
                                data-testid={`mission-summary-${s.category_id || "none"}`}
                                className="flex items-center justify-between gap-3 rounded-xl border border-md-border bg-md-surface px-4 py-3"
                            >
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="h-3 w-3 flex-none rounded-full" style={{ background: s.color }} />
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-md-text">{s.label}</p>
                                        <p className="text-[11px] text-md-text-2">{s.count} carte{s.count > 1 ? "s" : ""}</p>
                                    </div>
                                </div>
                                <p className="font-mono text-sm font-bold text-md-text">{fmtEur(s.total_eur)}</p>
                            </li>
                        ))}
                    </ul>
                </AppSection>
            )}

            {/* Items list */}
            <AppSection title={empty ? "Mission vide" : `${item_count} carte${item_count > 1 ? "s" : ""}`}>
                {empty ? (
                    <EmptyState
                        icon={<Compass className="h-6 w-6" />}
                        title="Aucune carte ajoutée"
                        description="Importe tes cartes manquantes ou explore un set pour les marquer puis ajoute-les."
                        action={
                            !locked && (
                                <div className="flex flex-wrap justify-center gap-2">
                                    <CTAButton onClick={addFromMissing} loading={busy} icon={<Sparkles className="h-4 w-4" />} testId="mission-detail-add-from-missing">
                                        Importer mes cartes manquantes
                                    </CTAButton>
                                    <CTAButton variant="outline" onClick={() => navigate("/app/explorer")} testId="mission-detail-go-explorer">
                                        Ouvrir l’Explorer
                                    </CTAButton>
                                </div>
                            )
                        }
                    />
                ) : (
                    <ul className="space-y-2" data-testid="mission-items-list">
                        {items.map((it) => (
                            <li
                                key={it._id}
                                data-testid={`mission-item-${it._id}`}
                                className="flex items-center justify-between gap-3 rounded-xl border border-md-border bg-md-surface px-3 py-2.5"
                            >
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-md-text">
                                        <span className="mr-2 font-mono text-[11px] text-md-text-2">{it.card_local_id}</span>
                                        {it.card_name}
                                    </p>
                                    <p className="mt-0.5 text-[11px] text-md-text-2">{it.rarity_label || "—"} · {it.variant_type}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs font-bold text-md-text">{fmtEur(it.price_eur)}</span>
                                    {!locked && (
                                        <button
                                            onClick={() => removeItem(it._id)}
                                            data-testid={`mission-item-remove-${it._id}`}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-md-text-2 hover:bg-md-surface-2 hover:text-md-danger"
                                            aria-label="Retirer"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </AppSection>

            {/* Tools — only while unlocked */}
            {!locked && !empty && (
                <AppSection>
                    <div className="flex flex-wrap gap-2">
                        <CTAButton variant="outline" size="sm" onClick={addFromMissing} loading={busy} icon={<Sparkles className="h-3 w-3" />} testId="mission-detail-add-more-missing">
                            Ajouter mes manquants
                        </CTAButton>
                        <CTAButton variant="outline" size="sm" onClick={recompute} loading={busy} testId="mission-detail-recompute">
                            Recalculer
                        </CTAButton>
                        <CTAButton variant="ghost" size="sm" onClick={() => setConfirmDelete(true)} icon={<Trash2 className="h-3 w-3" />} testId="mission-detail-delete">
                            Supprimer
                        </CTAButton>
                    </div>
                </AppSection>
            )}

            {locked && (
                <AppSection>
                    <div className="rounded-2xl border border-md-secondary/40 bg-md-surface px-5 py-4">
                        <div className="flex items-center gap-2">
                            <Lock className="h-4 w-4 text-md-secondary" />
                            <p className="text-sm font-semibold text-md-text">Mission verrouillée</p>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-md-text-2">
                            Pour protéger le temps des dresseurs, une mission est figée dès qu’elle est lancée. Pour modifier, crée une nouvelle mission.
                        </p>
                    </div>
                </AppSection>
            )}

            <div className="h-24" />

            <AppStickyFooter>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => navigate("/app/missions")}
                        data-testid="mission-detail-back"
                        className="inline-flex h-12 flex-1 items-center justify-center rounded-xl border border-md-border bg-transparent text-sm font-semibold text-md-text hover:bg-md-surface-2"
                    >
                        Plus tard
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowLaunchInfo(true)}
                        disabled={empty}
                        data-testid="mission-detail-launch"
                        className="inline-flex h-12 flex-[2] items-center justify-center gap-2 rounded-xl bg-md-primary text-sm font-semibold text-md-primary-fg hover:opacity-90 disabled:opacity-50"
                    >
                        <Rocket className="h-4 w-4" />
                        Lancer la mission
                    </button>
                </div>
            </AppStickyFooter>

            <AppModal
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title="Supprimer cette mission ?"
                subtitle="Tu peux toujours en re-créer une depuis tes manquants."
                variant="destructive"
                icon={<Trash2 className="h-5 w-5" />}
                testId="mission-delete-modal"
                primaryAction={{
                    label: busy ? "Suppression…" : "Supprimer",
                    onClick: doDelete,
                    disabled: busy,
                    variant: "destructive",
                }}
                secondaryAction={{
                    label: "Annuler",
                    onClick: () => setConfirmDelete(false),
                }}
            >
                <p className="text-sm text-md-text-2">Cette action est définitive. Toutes les cartes ajoutées seront retirées.</p>
            </AppModal>

            <AppModal
                open={showLaunchInfo}
                onOpenChange={setShowLaunchInfo}
                title="Lancement en Phase 3B"
                subtitle="Le matching dresseur arrive très bientôt."
                variant="info"
                icon={<Rocket className="h-5 w-5" />}
                testId="mission-launch-info-modal"
            >
                <div className="space-y-3 text-sm text-md-text-2">
                    <p>Phase 3A pose les fondations : préparer ta mission, voir le résumé et l’estimation, modifier librement.</p>
                    <p>Phase 3B activera le verrouillage doctrinaire, le matching dresseur, l’élargissement intelligent et le lifecycle complet.</p>
                    <div className="rounded-lg border border-md-secondary/30 bg-md-secondary/5 p-3">
                        <p className="md-overline mb-1 text-md-secondary">DOCTRINE</p>
                        <p className="italic">« Une mission est figée dès qu’elle est lancée — pour respecter le temps du dresseur. »</p>
                    </div>
                </div>
            </AppModal>
        </>
    );
}
